import { Ajax, Ajax2 } from '@shared/Ajax';
import { LegacyStateService } from '@shared/user-state/legacy/LegacyStateService';
import { UsersService } from '@shared/services/UsersService';
import { Notify } from '@shared/ui-native/Notify';
import { downloadURL } from '@shared/utils/utils';

const API_ROOT = 'api/saved-view/';

/** The BCF schema versions the export endpoint accepts. */
export type BcfVersion = '2.1' | '3.0';

export const PageStateService = {
    create(item: IPageState): Promise<IPageState> {
        return Ajax2({
            url: kuura.getContextPath() + '/api/saved-view',
            method: 'post',
            contentType: 'json',
            data: serialize(item),
        }).then((resp: { pageState: IPageState & { data: string } }) => {
            const created = deserialize(resp.pageState);
            if (!created.thumbnail?.startsWith('data:image')) {
                created.thumbnail = '';
            }
            PageStateService.clampTimestamps(created);
            return created;
        });
    },

    fetch(folderId: number, page: IPageStatePage, fetchData = true, fetchThumbnail = true): JQueryPromise<IPageState[]> {
        return Ajax({
            url: 'api/saved-views',
            data: {
                folderId,
                page,
                fetchData,
                fetchThumbnail,
            },
        }).then((resp: { pageStates: (IPageState & { data?: string })[] }) => {
            if (resp.pageStates) {
                for (let pageState of resp.pageStates) {
                    pageState = deserialize(pageState);
                    if (!pageState.thumbnail?.startsWith('data:image')) {
                        pageState.thumbnail = '';
                    }
                    PageStateService.clampTimestamps(pageState);
                }
            }
            return resp.pageStates;
        });
    },

    /**
     * @param id - either id or uuid
     */
    fetchItem(id: string | number, fetchThumbnail: boolean = true, updateModels: boolean = true, signal?: AbortSignal): Promise<IPageState> {
        const isUUID = typeof id !== 'number';

        return Ajax2({
            url: isUUID ? `${API_ROOT}uuid/${id}` : API_ROOT + id,
            signal,
            data: {
                [isUUID ? 'uuid' : 'pageStateId']: id,
                updateModels,
                fetchThumbnail,
            },
        }).then((resp: { pageState: IPageState & { data: string } }) => {
            if (resp.pageState) {
                resp.pageState.data = JSON.parse(resp.pageState.data);
                if (!resp.pageState.thumbnail?.startsWith('data:image')) {
                    resp.pageState.thumbnail = '';
                }
                PageStateService.clampTimestamps(resp.pageState);
            }

            return resp.pageState;
        });
    },

    // check if user has project
    // ask to switch project
    fetchStateWithProjectCheck: async (uuid: string, confirmWhenStateIsValid = true): Promise<IPageState> => {
        let sharedState: IPageState = null;
        try {
            sharedState = await PageStateService.fetchItem(uuid, false);
        } catch (e) {
            const msg = {
                403: __('js_saved_views.load.belongs_to_other_project_no_access'),
                404: __('js_saved_views.load.not_found'),
            };
            Notify.warn(msg[e.data?.errorCode] ?? null);
            return null;
        }

        if (sharedState.projectId != kuura.getProjectId()) {
            const userProjects: IProject[] = await UsersService.fetchProjectsForCurrentUser();
            const targetProject: IProject = userProjects.find(p => p.id == sharedState.projectId);

            if (targetProject) {
                if (confirm(__('js_saved_views.load.belongs_to_other_project_switch', targetProject.name))) {
                    await UsersService.changeProject(targetProject.id);
                    window.location.reload();
                    return null;
                } else {
                    return null;
                }
            } else {
                Notify.warn(__('js_saved_views.load.belongs_to_other_project_no_access'));
                return null;
            }
        }

        if (confirmWhenStateIsValid) {
            if (confirm(__('js_saved_views.load.load_shared_state_ask'))) {
                return sharedState;
            } else {
                return null;
            }
        }

        return sharedState;
    },

    fetchStateOrLiveState: async (
        idOrUuid: number | string,
        page: IPageStatePage,
        askConfirmLoad = false,
        fallbackToLiveState = true,
        projectCheck = true
    ): Promise<IPageState> => {
        let state: IPageState = null;

        // load shared state from uuid
        if (typeof idOrUuid === 'string') {
            try {
                state = await PageStateService.fetchStateWithProjectCheck(idOrUuid, askConfirmLoad);
            } catch {
                console.warn('[ page state ] Initial shared state fetch failed');
            }
        }
        // load users live state
        else if (fallbackToLiveState) {
            try {
                state = await PageStateService.liveState.fetch(page);
            } catch {
                console.warn('[ page state ] Initial live state fetch failed');
            }
        }

        return state;
    },

    update(idOrUuid: number | string, item: Partial<IPageState>): Promise<IPageState> {
        delete item.id;
        delete item.uuid;

        if (item.page === 'MAP3D_LEGACY') {
            return Ajax2({
                url: `${kuura.getContextPath()}/visualization/screenshot/${idOrUuid}/name.json`,
                method: 'post',
                data: {
                    name: item.name,
                },
            });
        } else {
            return Ajax2({
                url: `${kuura.getContextPath()}/${API_ROOT}uuid/${idOrUuid}`,
                method: 'post',
                contentType: 'json',
                data: serialize(item as IPageState),
            }).then((resp: { pageState: IPageState & { data: string } }) => {
                const updated = deserialize(resp.pageState);
                if (!updated.thumbnail?.startsWith('data:image')) {
                    updated.thumbnail = '';
                }
                PageStateService.clampTimestamps(updated);
                return updated;
            });
        }
    },

    remove(item: Partial<IPageState>): JQueryPromise<void> {
        if (item.page === 'MAP3D_LEGACY') {
            return LegacyStateService.remove(item.id);
        } else {
            return Ajax({
                url: API_ROOT + item.uuid,
                method: 'delete',
            });
        }
    },

    fetchFolderCounts(
        page: IPageStatePage,
        folderId: number
    ): Promise<{
        [key: number]: number;
    }> {
        return Ajax2({
            url: `${kuura.getContextPath()}/${API_ROOT}${page}/folder-counts`,
            method: 'get',
            data: {
                folderId,
                projectId: kuura.getProjectId(),
            },
        }).then((resp: { counts: { [key: number]: number } }) => {
            return resp.counts;
        });
    },

    lock(uuid: string): Promise<IPageState> {
        return Ajax2({
            url: `${kuura.getContextPath()}/api/page-state/uuid/${uuid}/lock`,
            method: 'post',
        });
    },

    unlock(uuid: string): Promise<IPageState> {
        return Ajax2({
            url: `${kuura.getContextPath()}/api/page-state/uuid/${uuid}/unlock`,
            method: 'post',
        });
    },

    /**
     * Export one or more saved views as a single BCF archive (.bcfzip), streamed directly
     * to the browser as a download. 2.1 is the CAD-plugin interop version (Solibri, Revit,
     * BIMcollab ZOOM); 3.0 is the current buildingSMART standard.
     */
    exportBcf(uuids: string[], version: BcfVersion = '2.1'): void {
        const params = [...uuids.map(u => `uuid=${encodeURIComponent(u)}`), `version=${encodeURIComponent(version)}`];
        const url = `${kuura.getContextPath()}/api/bcf/export?${params.join('&')}`;
        downloadURL(url);
    },

    clampTimestamps: (state: IPageState) => {
        // when state is locked we want filter dates to be clamped to locked time, and absolute (no preset)
        if (!state.lockedAt) return;

        const clampTimestamp = (timestamp: { to: number; preset: number }) => {
            if (!timestamp) return;
            timestamp.preset = null;
            if (timestamp.to == null || timestamp.to > state.lockedAt) {
                timestamp.to = state.lockedAt;
            }
        };

        clampTimestamp(state.data?.panel?.documents?.timestamp);
        clampTimestamp(state.data?.panel?.images?.timestamp);
        clampTimestamp(state.data?.panel?.logpoints?.timestamp);
    },

    liveState: {
        fetch(page: IPageStatePage): Promise<IPageState> {
            return new Promise((done, fail) => {
                Ajax({
                    url: API_ROOT + 'user-live-state/' + page,
                    method: 'get',
                })
                    .then((resp: { pageState: IPageState }) => {
                        done(deserialize(resp.pageState));
                    })
                    .fail(() => {
                        fail();
                    });
            });
        },

        saveOrUpdate(pageState: IPageState): JQueryPromise<void> {
            const liveState = {
                ...pageState,
                liveState: true,
                visibility: 'PRIVATE' as any,
                timestamp: Date.now(),
                projectId: kuura.getProjectId(),
            };

            return Ajax({
                url: API_ROOT + 'user-live-state',
                method: 'post',
                processData: false,
                data: serialize(liveState),
            });
        },
    },
};

function serialize(pageState: IPageState): IPageState {
    pageState = JSON.parse(JSON.stringify(pageState));
    if (pageStatHasData(pageState)) {
        pageState.data = JSON.stringify(pageState.data) as any;
    }
    return pageState;
}

function deserialize(pageState: IPageState): IPageState {
    if (pageStatHasData(pageState)) {
        pageState.data = JSON.parse(pageState.data);
    }
    return pageState;
}

function pageStatHasData(pageState: IPageState): boolean {
    return pageState && pageState.data && (typeof pageState.data === 'string' || typeof pageState.data === 'object');
}
